import type { DeviceSummary } from '../../types/org-console';

const UUID_LIKE_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const timestampMs = (value?: string | null): number | null => {
  if (!value) {
    return null;
  }
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) {
    return null;
  }
  return timestamp;
};

export interface DeviceHealthGhostCandidate {
  bgpTotal: number;
  cpu: number | null;
  device: DeviceSummary;
  interfacesTotal: number;
  mem: number | null;
  staleMinutes: number;
  telemetryReporting: boolean;
  uptimeSeconds: number | null;
}

export interface DeviceHealthAggregate {
  up: number;
  total: number;
}

export interface OrgDeviceHealthRowShape {
  id: string;
  device: string;
  site: string;
  cpu: number | null;
  mem: number | null;
  uptimeSeconds: number | null;
  interfacesUp: number;
  interfacesTotal: number;
  bgpUp: number;
  bgpTotal: number;
  lastTelemetryAt?: string;
}

export const shouldHideGhostDeviceHealthRow = ({
  bgpTotal,
  cpu,
  device,
  interfacesTotal,
  mem,
  staleMinutes,
  telemetryReporting,
  uptimeSeconds
}: DeviceHealthGhostCandidate): boolean => {
  const displayName = (device.displayName ?? device.name)?.trim();
  const deviceId = device.id?.trim();
  const siteId = device.siteId?.trim();
  const hasHumanMeaningfulName = Boolean(displayName || (deviceId && !UUID_LIKE_PATTERN.test(deviceId)));
  const hasOperationalEvidence =
    telemetryReporting ||
    cpu != null ||
    mem != null ||
    uptimeSeconds != null ||
    interfacesTotal > 0 ||
    bgpTotal > 0;
  const lastSeen = timestampMs(device.lastSeenAt);
  const staleCutoffMs = staleMinutes * 60_000;
  const isExplicitlyStale = lastSeen != null && Date.now() - lastSeen > staleCutoffMs;
  const lacksInventoryContext = !siteId;

  return !hasHumanMeaningfulName && !hasOperationalEvidence && isExplicitlyStale && lacksInventoryContext;
};

export interface BuildOrgDeviceHealthRowArgs {
  bgpByDevice: ReadonlyMap<string, DeviceHealthAggregate>;
  cpuByDevice: ReadonlyMap<string, number | null>;
  device: DeviceSummary;
  interfacesByDevice: ReadonlyMap<string, DeviceHealthAggregate>;
  memByDevice: ReadonlyMap<string, number | null>;
  orgSiteNameById: ReadonlyMap<string, string>;
  staleMinutes: number;
  lastTelemetryAtByDevice: ReadonlyMap<string, string>;
  telemetryReportingDeviceIds: ReadonlySet<string>;
  uptimeByDevice: ReadonlyMap<string, number | null>;
}

export interface BuildOrgDeviceHealthRowsArgs {
  bgpByDevice: ReadonlyMap<string, DeviceHealthAggregate>;
  cpuByDevice: ReadonlyMap<string, number | null>;
  devices: readonly DeviceSummary[];
  interfacesByDevice: ReadonlyMap<string, DeviceHealthAggregate>;
  memByDevice: ReadonlyMap<string, number | null>;
  orgSiteNameById: ReadonlyMap<string, string>;
  staleMinutes: number;
  lastTelemetryAtByDevice: ReadonlyMap<string, string>;
  telemetryReportingDeviceIds: ReadonlySet<string>;
  uptimeByDevice: ReadonlyMap<string, number | null>;
}

export const buildOrgDeviceHealthRow = ({
  bgpByDevice,
  cpuByDevice,
  device,
  interfacesByDevice,
  memByDevice,
  orgSiteNameById,
  staleMinutes,
  lastTelemetryAtByDevice,
  telemetryReportingDeviceIds,
  uptimeByDevice
}: BuildOrgDeviceHealthRowArgs): OrgDeviceHealthRowShape | null => {
  const id = device.id?.trim();
  if (!id) {
    return null;
  }
  const iface = interfacesByDevice.get(id) ?? { up: 0, total: 0 };
  const bgp = bgpByDevice.get(id) ?? { up: 0, total: 0 };
  const cpu = cpuByDevice.get(id) ?? null;
  const mem = memByDevice.get(id) ?? null;
  const uptimeSeconds = uptimeByDevice.get(id) ?? null;
  const siteId = device.siteId?.trim();

  if (
    shouldHideGhostDeviceHealthRow({
      device,
      cpu,
      mem,
      uptimeSeconds,
      interfacesTotal: iface.total,
      bgpTotal: bgp.total,
      telemetryReporting: telemetryReportingDeviceIds.has(id),
      staleMinutes
    })
  ) {
    return null;
  }

  return {
    id,
    device: device.displayName ?? device.name ?? device.hostname ?? id,
    site: siteId ? orgSiteNameById.get(siteId) ?? siteId : '-',
    cpu,
    mem,
    uptimeSeconds,
    interfacesUp: iface.up,
    interfacesTotal: iface.total,
    bgpUp: bgp.up,
    bgpTotal: bgp.total,
    lastTelemetryAt: lastTelemetryAtByDevice.get(id)
  };
};

export const buildOrgDeviceHealthRows = ({
  bgpByDevice,
  cpuByDevice,
  devices,
  interfacesByDevice,
  memByDevice,
  orgSiteNameById,
  staleMinutes,
  lastTelemetryAtByDevice,
  telemetryReportingDeviceIds,
  uptimeByDevice
}: BuildOrgDeviceHealthRowsArgs): OrgDeviceHealthRowShape[] =>
  devices
    .flatMap((device) => {
      const row = buildOrgDeviceHealthRow({
        device,
        interfacesByDevice,
        bgpByDevice,
        cpuByDevice,
        memByDevice,
        uptimeByDevice,
        orgSiteNameById,
        lastTelemetryAtByDevice,
        telemetryReportingDeviceIds,
        staleMinutes
      });
      return row ? [row] : [];
    })
    .sort((left, right) => (right.cpu ?? -1) - (left.cpu ?? -1) || left.device.localeCompare(right.device));
